import { httpApi } from 'api/http.api';
import {
  EmployeeIncidences,
  Incidence,
  IncidenceCategory,
  IncidenceCategoryPayload,
  ReconcileIncidencesPayload,
  IncidencesResponseType,
  BulkReconciliationPayload,
  BulkReconciliationPreview,
  BulkReconciliationResult,
  EmployeeReconciliationOutcome,
} from 'types/incidences';
import { ReportFilters } from 'types/reports';
import { ApiError, ResponseGeneral } from 'types';
import {
  GetEmployeeIncidencesProps,
  GetEmployeeIncidencesByDateRangeProps,
} from 'interfaces/employee.interfaces';
import { AxiosResponse } from 'axios';

const controller = `incidences`;

export const createOne = (
  values: Incidence
): Promise<AxiosResponse<Incidence>> =>
  httpApi.post<Incidence>(`${controller}`, values).then((res) => res);

export const getIncidencesList = (
  filters: ReportFilters,
  page: number,
  limit: number
): Promise<AxiosResponse<IncidencesResponseType>> => {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  }).toString();

  return httpApi
    .post<IncidencesResponseType>(`${controller}/getIncidences?${params}`, {
      ...filters,
    })
    .then((res) => res);
};

export const updateOne = (id: string): Promise<AxiosResponse<Incidence>> =>
  httpApi.put<Incidence>(`${controller}/${id}`, {}).then((res) => res);

export const removeOne = (id: string): Promise<AxiosResponse<Incidence>> =>
  httpApi.delete<Incidence>(`${controller}/${id}`, {}).then((res) => res);

///// Categories

export const createCategory = (
  values: IncidenceCategoryPayload
): Promise<AxiosResponse<IncidenceCategory>> =>
  httpApi
    .post<IncidenceCategory>(`${controller}/category`, values)
    .then((res) => res);

export const getCategories = (): Promise<AxiosResponse<IncidenceCategory[]>> =>
  httpApi
    .get<IncidenceCategory[]>(`${controller}/category`, {})
    .then((res) => res);

export const getCategory = (
  id: string
): Promise<AxiosResponse<IncidenceCategory>> =>
  httpApi
    .get<IncidenceCategory>(`${controller}/category/${id}`, {})
    .then((res) => res);

export const updateCategory = (
  values: IncidenceCategoryPayload,
  id: string
): Promise<AxiosResponse<IncidenceCategory>> =>
  httpApi
    .put<IncidenceCategory>(`${controller}/category/${id}`, values)
    .then((res) => res);

export const removeCategory = (
  id: string
): Promise<AxiosResponse<IncidenceCategory>> =>
  httpApi
    .delete<IncidenceCategory>(`${controller}/category/${id}`, {})
    .then((res) => res);

export const reconcileIncidences = (
  payload: ReconcileIncidencesPayload
): Promise<AxiosResponse<ResponseGeneral>> =>
  httpApi
    .post<ResponseGeneral>(`${controller}/reconcileIncidences`, {
      ...payload,
    })
    .then((res) => res);

export const previewBulkReconciliation = (
  payload: BulkReconciliationPayload
): Promise<AxiosResponse<BulkReconciliationPreview>> =>
  httpApi
    .post<BulkReconciliationPreview>(
      `${controller}/reconcile-bulk/preview`,
      payload
    )
    .then((res) => res);

export const executeBulkReconciliation = (
  payload: BulkReconciliationPayload
): Promise<AxiosResponse<BulkReconciliationResult>> =>
  httpApi
    .post<BulkReconciliationResult>(`${controller}/reconcile-bulk`, payload)
    .then((res) => res);

export const reconcileBulkEmployee = async (
  payload: BulkReconciliationPayload & { employee: string }
): Promise<EmployeeReconciliationOutcome> => {
  const { data, status } = await httpApi.post<unknown>(
    `${controller}/reconcile-bulk/employee`,
    payload,
    {
      timeout: 60000,
      // Keep the queue on screen after lost authorization; do not follow the
      // global interceptor's automatic logout/forbidden redirect for this call.
      validateStatus: (value) =>
        (value >= 200 && value < 300) || value === 401 || value === 403,
    }
  );
  if (status === 401 || status === 403) {
    throw new ApiError(
      status,
      'Tu sesión o tus permisos cambiaron. El proceso está pausado.'
    );
  }
  if (!data || typeof data !== 'object')
    throw new Error('Unconfirmed reconciliation');
  const result = data as EmployeeReconciliationOutcome;
  const failedCodes = [
    'NOT_ELIGIBLE',
    'INVALID_CONTRACT_KIND',
    'RECONCILIATION_FAILED',
    'SERVICE_UNAVAILABLE',
  ];
  if (
    result.protocol !== 'employee-reconciliation-v1' ||
    result.employeeId !== payload.employee ||
    typeof result.pauseQueue !== 'boolean' ||
    !(result.outcome === 'completed'
      ? result.code === 'COMPLETED' && !result.pauseQueue
      : result.outcome === 'failed' && failedCodes.includes(result.code))
  )
    throw new Error('Unconfirmed reconciliation');
  return result;
};

export const getEmployeeIncidencesByYear = ({
  employeeId,
  year,
}: GetEmployeeIncidencesProps): Promise<AxiosResponse<EmployeeIncidences[]>> =>
  httpApi
    .get<
      EmployeeIncidences[]
    >(`${controller}/get-employee-incidences-by-year/${year}/${employeeId}`, {})
    .then((res) => res);

export const getEmployeeWeeklyIncidences = (
  payload: GetEmployeeIncidencesByDateRangeProps
): Promise<AxiosResponse<EmployeeIncidences[]>> =>
  httpApi
    .post<
      EmployeeIncidences[]
    >(`${controller}/get-employee-weekly-incidences`, { ...payload })
    .then((res) => res);
